import React, { useState } from "react";
import {
  Building2,
  Shield,
  Scale,
  Calculator,
  Receipt,
  Users,
  Globe,
  Lock,
  FileCheck,
  Award,
  DollarSign,
  UserCheck,
  ChevronDown,
  ChevronUp,
  Zap,
  Target,
  Lightbulb,
  Phone,
  Mail,
  MessageSquare,
  BookOpen,
  Video,
  HelpCircle,
  ArrowRight,
} from "lucide-react";

interface GuideTopic {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  sections: GuideSection[];
}

interface GuideSection {
  heading: string;
  content: string[];
}

interface ServiceDomain {
  id: number;
  title: string;
  icon: React.ReactNode;
  description: string;
  keyFeatures: string[];
  useCases: string[];
}

const UserSystemGuide: React.FC = () => {
  const [expandedTopic, setExpandedTopic] = useState<string | null>(
    "getting-started"
  );
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<
    "overview" | "services" | "support"
  >("overview");

  const guideTopics: GuideTopic[] = [
    {
      id: "getting-started",
      title: "Getting Started",
      description:
        "Begin your BizServe journey with these essential first steps",
      icon: <Lightbulb className="w-6 h-6" />,
      sections: [
        {
          heading: "Account Setup",
          content: [
            "Create your BizServe account with your business email",
            "Complete your business profile with essential information",
            "Verify your email address to activate all features",
            "Set up two-factor authentication for enhanced security",
          ],
        },
        {
          heading: "Dashboard Navigation",
          content: [
            "Access your personalized dashboard upon login",
            "View all available services organized by domain",
            "Monitor your progress on active tasks and projects",
            "Access quick links to frequently used features",
          ],
        },
        {
          heading: "First Steps",
          content: [
            "Explore the service domains relevant to your business",
            "Start with foundational services like Company Formation or Legal Setup",
            "Connect with an expert for personalized guidance",
            "Complete your business profile for tailored recommendations",
          ],
        },
      ],
    },
    {
      id: "platform-features",
      title: "Platform Features & Tools",
      description: "Master the powerful tools available on BizServe",
      icon: <Zap className="w-6 h-6" />,
      sections: [
        {
          heading: "Service Domains",
          content: [
            "Access 12+ specialized service domains tailored to business needs",
            "Each domain contains tools, templates, and expert guidance",
            "Domains work together seamlessly for integrated solutions",
            "Filter and search across domains to find what you need",
          ],
        },
        {
          heading: "Document Management",
          content: [
            "Upload and organize business documents securely",
            "Access pre-built templates for contracts, forms, and agreements",
            "Generate custom documents based on your business type",
            "Download and export documents in multiple formats",
          ],
        },
        {
          heading: "Expert Connection",
          content: [
            "Connect with certified accountants, attorneys, and HR specialists",
            "Schedule consultations directly through the platform",
            "Receive tailored advice for your specific situation",
            "Access our network of 100+ vetted professionals",
          ],
        },
        {
          heading: "Progress Tracking",
          content: [
            "Monitor completion status of all your business tasks",
            "Receive reminders for upcoming deadlines",
            "Track compliance requirements and certifications",
            "View historical records and completed actions",
          ],
        },
      ],
    },
    {
      id: "service-workflows",
      title: "Common Service Workflows",
      description: "Learn how to use BizServe for typical business scenarios",
      icon: <Target className="w-6 h-6" />,
      sections: [
        {
          heading: "Starting a New Business",
          content: [
            "1. Use Company Formation to select your business structure (LLC, C-Corp, etc.)",
            "2. Obtain your EIN through the integrated tax system",
            "3. Set up business insurance with guided recommendations",
            "4. Create legal contracts and agreements",
            "5. Establish accounting records with Bookkeeping",
          ],
        },
        {
          heading: "Government Contracting",
          content: [
            "1. Register on SAM.gov with step-by-step guidance",
            "2. Obtain necessary certifications (SOC 2, CMMC, etc.)",
            "3. Understand DCAA compliance requirements",
            "4. Prepare proposals with expert support",
            "5. Track certifications and renewals",
          ],
        },
        {
          heading: "Employee Onboarding",
          content: [
            "1. Access the HR Portal for streamlined onboarding",
            "2. Generate offer letters and employment agreements",
            "3. Prepare onboarding documents and checklists",
            "4. Set up payroll and benefits",
            "5. Manage I-9 verification and compliance",
          ],
        },
        {
          heading: "Tax Compliance & Planning",
          content: [
            "1. Set up quarterly tax filing schedule",
            "2. Reconcile accounts and prepare tax documents",
            "3. Identify tax deductions and credits",
            "4. Plan for estimated tax payments",
            "5. File federal and state returns with guidance",
          ],
        },
      ],
    },
    {
      id: "security-compliance",
      title: "Security & Compliance",
      description: "Understand how BizServe protects your business",
      icon: <Lock className="w-6 h-6" />,
      sections: [
        {
          heading: "Data Protection",
          content: [
            "All data is encrypted both in transit and at rest",
            "Industry-standard SSL/TLS encryption protects your information",
            "Regular security audits ensure compliance with best practices",
            "Your documents are backed up and protected from loss",
          ],
        },
        {
          heading: "Compliance Standards",
          content: [
            "BizServe meets SOC 2 Type II compliance requirements",
            "GDPR compliant data handling for international users",
            "HIPAA considerations for sensitive business data",
            "Regular compliance updates reflect changing regulations",
          ],
        },
        {
          heading: "Access Control",
          content: [
            "Role-based access control for team members",
            "Two-factor authentication for enhanced account security",
            "Session management and automatic logout for security",
            "Audit logs track all account activities",
          ],
        },
      ],
    },
    {
      id: "best-practices",
      title: "Best Practices & Tips",
      description: "Maximize your success with BizServe",
      icon: <BookOpen className="w-6 h-6" />,
      sections: [
        {
          heading: "Organization Tips",
          content: [
            "Keep your business profile updated for accurate recommendations",
            "Organize documents into folders by category or year",
            "Use labels and tags to quickly find important documents",
            "Archive completed projects to reduce clutter",
          ],
        },
        {
          heading: "Document Management",
          content: [
            "Review all generated documents before finalizing",
            "Keep backups of important contracts and agreements",
            "Set reminders for document renewal dates",
            "Use version control for evolving agreements",
          ],
        },
        {
          heading: "Expert Engagement",
          content: [
            "Prepare your questions before expert consultations",
            "Share relevant documents with experts for better advice",
            "Follow through on recommendations promptly",
            "Schedule regular check-ins for ongoing guidance",
          ],
        },
        {
          heading: "Compliance Maintenance",
          content: [
            "Review compliance calendars monthly for upcoming deadlines",
            "Set automatic reminders for renewal dates",
            "Keep certification documents organized and accessible",
            "Update business information when changes occur",
          ],
        },
      ],
    },
  ];

  const serviceDomains: ServiceDomain[] = [
    {
      id: 1,
      title: "Company Formation",
      icon: <Building2 className="w-6 h-6" />,
      description: "Start your business with confidence",
      keyFeatures: [
        "Business entity selection (LLC, C-Corp, S-Corp)",
        "EIN acquisition",
        "State registration and licensing",
        "Operating agreements and bylaws",
      ],
      useCases: [
        "Registering a new business",
        "Changing business structure",
        "Opening in new states",
      ],
    },
    {
      id: 2,
      title: "Bookkeeping",
      icon: <Calculator className="w-6 h-6" />,
      description: "Maintain accurate financial records",
      keyFeatures: [
        "Income and expense tracking",
        "Account reconciliation",
        "Financial reports generation",
        "Payroll setup and management",
      ],
      useCases: [
        "Setting up accounting system",
        "Monthly reconciliation",
        "Tax preparation",
      ],
    },
    {
      id: 3,
      title: "Tax Services",
      icon: <Receipt className="w-6 h-6" />,
      description: "Manage tax obligations efficiently",
      keyFeatures: [
        "Federal and state tax filings",
        "Quarterly payment planning",
        "Deduction maximization",
        "Tax strategy consultation",
      ],
      useCases: [
        "Quarterly tax payments",
        "Annual tax filing",
        "Tax planning consultation",
      ],
    },
    {
      id: 4,
      title: "Legal Services",
      icon: <Scale className="w-6 h-6" />,
      description: "Protect your business with legal guidance",
      keyFeatures: [
        "Contract templates and review",
        "Trademark and IP protection",
        "Compliance documentation",
        "Legal expert access",
      ],
      useCases: [
        "Creating business contracts",
        "IP protection",
        "Legal compliance",
      ],
    },
    {
      id: 5,
      title: "Human Resources",
      icon: <Users className="w-6 h-6" />,
      description: "Manage employees effectively",
      keyFeatures: [
        "Employee onboarding",
        "Benefits administration",
        "Payroll processing",
        "Compliance management",
      ],
      useCases: [
        "Hiring new employees",
        "Onboarding automation",
        "Benefits management",
      ],
    },
    {
      id: 6,
      title: "Business Insurance",
      icon: <Shield className="w-6 h-6" />,
      description: "Protect your business and assets",
      keyFeatures: [
        "Insurance recommendations",
        "Policy comparison tools",
        "GovCon compliance coverage",
        "Claims assistance",
      ],
      useCases: [
        "Coverage needs assessment",
        "Policy selection",
        "Compliance requirements",
      ],
    },
    {
      id: 7,
      title: "Government Contracting",
      icon: <FileCheck className="w-6 h-6" />,
      description: "Access federal contracting opportunities",
      keyFeatures: [
        "SAM.gov registration",
        "DCAA compliance guidance",
        "Certification management",
        "Proposal support",
      ],
      useCases: [
        "GovCon entry",
        "Certification renewals",
        "Compliance maintenance",
      ],
    },
    {
      id: 8,
      title: "Compliance & Certifications",
      icon: <Award className="w-6 h-6" />,
      description: "Meet industry standards and requirements",
      keyFeatures: [
        "SOC 2 compliance guidance",
        "ISO certification support",
        "OSHA requirements",
        "HIPAA compliance",
      ],
      useCases: [
        "Achieving certifications",
        "Maintaining compliance",
        "Audit preparation",
      ],
    },
  ];

  const supportChannels = [
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Live Chat Support",
      description: "Get instant help from our support team",
      action: "Start Chat",
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Email Support",
      description: "Send detailed questions and receive thorough responses",
      action: "Send Email",
    },
    {
      icon: <Phone className="w-8 h-8" />,
      title: "Phone Support",
      description: "Speak directly with a BizServe specialist",
      action: "Call Now",
    },
    {
      icon: <Video className="w-8 h-8" />,
      title: "Video Tutorials",
      description: "Learn through step-by-step video guides",
      action: "Watch Now",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border-b">
        <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            BizServe User System Guide
          </h1>
          <p className="text-lg text-muted-foreground">
            Learn how to use BizServe to manage and grow your business
            effectively
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b sticky top-0 bg-card z-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-6 py-4 font-medium border-b-2 transition-colors ${
                activeTab === "overview"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("services")}
              className={`px-6 py-4 font-medium border-b-2 transition-colors ${
                activeTab === "services"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab("support")}
              className={`px-6 py-4 font-medium border-b-2 transition-colors ${
                activeTab === "support"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Support
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            {guideTopics.map((topic) => (
              <div
                key={topic.id}
                className="border rounded-lg hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() =>
                    setExpandedTopic(
                      expandedTopic === topic.id ? null : topic.id
                    )
                  }
                  className="w-full px-6 py-4 flex items-start justify-between gap-4 hover:bg-muted transition-colors"
                >
                  <div className="flex items-start gap-4 flex-1 text-left">
                    <div className="text-primary mt-1 shrink-0">
                      {topic.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">
                        {topic.title}
                      </h3>
                      <p className="text-muted-foreground text-sm mt-1">
                        {topic.description}
                      </p>
                    </div>
                  </div>
                  {expandedTopic === topic.id ? (
                    <ChevronUp className="w-5 h-5 text-primary shrink-0 mt-1" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0 mt-1" />
                  )}
                </button>

                {expandedTopic === topic.id && (
                  <div className="border-t px-6 py-4 bg-muted/30 space-y-6">
                    {topic.sections.map((section, idx) => (
                      <div key={idx}>
                        <h4 className="font-semibold text-foreground mb-3">
                          {section.heading}
                        </h4>
                        <ul className="space-y-2">
                          {section.content.map((item, itemIdx) => (
                            <li
                              key={itemIdx}
                              className="flex gap-3 text-muted-foreground"
                            >
                              <ArrowRight className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === "services" && (
          <div className="space-y-4">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Available Services
              </h2>
              <p className="text-muted-foreground">
                Explore all the specialized domains available on BizServe
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {serviceDomains.map((service) => (
                <div
                  key={service.id}
                  className="border rounded-lg hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() =>
                      setExpandedService(
                        expandedService === service.id ? null : service.id
                      )
                    }
                    className="w-full px-6 py-4 flex items-start justify-between gap-4 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-start gap-4 flex-1 text-left">
                      <div className="text-primary shrink-0">
                        {service.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">
                          {service.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    {expandedService === service.id ? (
                      <ChevronUp className="w-5 h-5 text-primary shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
                    )}
                  </button>

                  {expandedService === service.id && (
                    <div className="border-t px-6 py-4 bg-muted/30 space-y-4">
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">
                          Key Features
                        </h4>
                        <ul className="space-y-1">
                          {service.keyFeatures.map((feature, idx) => (
                            <li
                              key={idx}
                              className="flex gap-2 text-muted-foreground text-sm"
                            >
                              <span className="text-primary">•</span>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">
                          Use Cases
                        </h4>
                        <ul className="space-y-1">
                          {service.useCases.map((useCase, idx) => (
                            <li
                              key={idx}
                              className="flex gap-2 text-muted-foreground text-sm"
                            >
                              <span className="text-primary">•</span>
                              <span>{useCase}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Support Tab */}
        {activeTab === "support" && (
          <div className="space-y-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Get Help
              </h2>
              <p className="text-muted-foreground">
                Multiple ways to get support and learn more about BizServe
              </p>
            </div>

            {/* Support Channels */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-12">
              {supportChannels.map((channel, idx) => (
                <div
                  key={idx}
                  className="border rounded-lg p-6 hover:shadow-lg transition-shadow text-center"
                >
                  <div className="text-primary mb-4 flex justify-center">
                    {channel.icon}
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">
                    {channel.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {channel.description}
                  </p>
                  <button className="text-primary font-medium hover:underline text-sm">
                    {channel.action}
                  </button>
                </div>
              ))}
            </div>

            {/* FAQ Section */}
            <div className="bg-muted/30 rounded-lg p-8">
              <div className="flex gap-3 mb-6">
                <HelpCircle className="w-6 h-6 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold text-foreground mb-2 text-lg">
                    Frequently Asked Questions
                  </h3>
                  <p className="text-muted-foreground">
                    Find answers to common questions about using BizServe
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  {
                    q: "How do I get started with BizServe?",
                    a: "Create an account, complete your business profile, and explore the service domains relevant to your business. Start with foundational services and connect with an expert for guidance.",
                  },
                  {
                    q: "Is my data secure on BizServe?",
                    a: "Yes, all data is encrypted both in transit and at rest using industry-standard SSL/TLS encryption. We maintain SOC 2 Type II compliance and regular security audits.",
                  },
                  {
                    q: "Can I connect with an expert?",
                    a: "Absolutely! You can schedule consultations with our network of 100+ certified accountants, attorneys, HR specialists, and business advisors.",
                  },
                  {
                    q: "What if I need help with a specific service?",
                    a: "Use our live chat, email, or phone support. Each service domain also includes built-in guidance and templates to help you succeed.",
                  },
                  {
                    q: "Can I work with my team on BizServe?",
                    a: "Yes, you can invite team members and assign role-based access controls so everyone can collaborate securely.",
                  },
                ].map((faq, idx) => (
                  <div key={idx} className="bg-card rounded p-4 border">
                    <p className="font-semibold text-foreground mb-2">
                      {faq.q}
                    </p>
                    <p className="text-muted-foreground text-sm">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Getting Started CTA */}
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border rounded-lg p-8 text-center">
              <h3 className="text-xl font-bold text-foreground mb-3">
                Ready to Get Started?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                Explore BizServe's features and discover how we can help you
                manage every aspect of your business.
              </p>
              <button className="bg-primary text-primary-foreground px-8 py-3 rounded-md font-medium hover:bg-primary/90 transition-colors">
                Start Exploring
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSystemGuide;
